import {genId} from '../Annotator/const';
import {PolyLabelProps} from '../../../../base-components/StudioAnnotator/types';
import {isPointInPolygon as isPointInPolygonUtil} from './../Annotator/utils/polygonsUtils';
import {
  Point,
  BoundingBox,
  ISegmentationDto,
  SegmentationData,
  SegmentationResult,
  GetPolygonsSettings,
} from './SegmentationDtoInterface';

export class SegmentationDto implements ISegmentationDto {
  imageId: string;
  data: SegmentationData;
  result: SegmentationResult;

  constructor(imageId: string, existingData?: Partial<ISegmentationDto>) {
    this.imageId = imageId;
    this.data = {
      polygons: existingData?.data?.polygons || [],
      boundingBoxes: existingData?.data?.boundingBoxes || [],
      positivePoints: existingData?.data?.positivePoints || [],
      negativePoints: existingData?.data?.negativePoints || [],
    };

    this.result = existingData?.result ?? null;
  }

  static fromJSON(data: any): SegmentationDto {
    const segmentation = new SegmentationDto(data.imageId);

    segmentation.data = {
      polygons: data.data.polygons.map((polygonData: any) => ({
        ...polygonData,
        points: polygonData.points.map((pointData: any) => ({
          x: pointData.x,
          y: pointData.y,
        })),
      })),
      boundingBoxes: data.data.boundingBoxes.map((boxData: any) => ({...boxData})),
      positivePoints: data.data.positivePoints.map((pointData: any) => ({
        x: pointData.x,
        y: pointData.y,
      })),
      negativePoints: data.data.negativePoints.map((pointData: any) => ({
        x: pointData.x,
        y: pointData.y,
      })),
    };

    return segmentation;
  }

  getBoundingBoxes(): BoundingBox[] {
    return this.data.boundingBoxes;
  }

  getPositivePoints(): Point[] {
    return this.data.positivePoints;
  }

  getNegativePoints(): Point[] {
    return this.data.negativePoints;
  }

  addPoint(point: Point, isPositive: boolean): void {
    const pointsArray = isPositive ? this.data.positivePoints : this.data.negativePoints;
    pointsArray.push(point);
  }

  removePoint(pointToRemove: Point): boolean {
    let missionAccomplished = false;

    this.data.positivePoints = this.data.positivePoints.filter(point => {
      let isNotPointToRemove = true;

      if (point.x === pointToRemove.x && point.y === pointToRemove.y) {
        isNotPointToRemove = false;
        missionAccomplished = true;
      }

      return isNotPointToRemove;
    });

    if (!missionAccomplished) {
      this.data.negativePoints = this.data.negativePoints.filter(point => {
        let isNotPointToRemove = true;

        if (point.x === pointToRemove.x && point.y === pointToRemove.y) {
          isNotPointToRemove = false;
          missionAccomplished = true;
        }

        return isNotPointToRemove;
      });
    }

    return missionAccomplished;
  }

  clearPoints() {
    this.data.positivePoints = [];
    this.data.negativePoints = [];
  }

  getBoundingBoxById(id: string): BoundingBox | undefined {
    return this.data.boundingBoxes.find(box => box.id === id);
  }

  addBoundingBox(box: BoundingBox): void {
    this.data.boundingBoxes.push(box);
  }

  removeBoundingBox(id: string): boolean {
    const initialLength = this.data.boundingBoxes.length;
    this.data.boundingBoxes = this.data.boundingBoxes.filter(box => box.id !== id);

    return this.data.boundingBoxes.length < initialLength;
  }

  updateBoundingBox(id: string, newBox: Partial<BoundingBox>): boolean {
    let missionAccomplished = false;

    this.data.boundingBoxes = this.data.boundingBoxes.map(box => {
      let updatedBox = {...box};

      if (box.id === id) {
        missionAccomplished = true;
        updatedBox = {...updatedBox, ...newBox, id};
      }

      return updatedBox;
    });

    return missionAccomplished;
  }

  updateSegmentationResultInMemory(): void {
    this.result = {
      id: genId('RESULT'),
      data: JSON.parse(JSON.stringify(this.data)),
    };
  }

  getPredictions(): PolyLabelProps[] {
    return this.data?.polygons?.filter(polygon => polygon.label) ?? [];
  }

  hasDataForPrediction(): boolean {
    return Boolean(
      this.data.positivePoints.length ||
        this.data.negativePoints.length ||
        this.data.boundingBoxes.length
    );
  }

  hasDataChanged(): boolean {
    const {polygons, ...dataWithoutPolygons} = this.data;
    const {polygons: resultPolygons, ...resultDataWithoutPolygons} =
      this.result?.data || {};

    return (
      JSON.stringify(resultDataWithoutPolygons) !== JSON.stringify(dataWithoutPolygons)
    );
  }

  clearSegmentationData(): void {
    this.data = {
      polygons: [],
      boundingBoxes: [],
      positivePoints: [],
      negativePoints: [],
    };
    this.updateSegmentationResultInMemory();
  }

  getPolygons(settings?: GetPolygonsSettings): PolyLabelProps[] {
    const {includeEmpty = true, includeLabeled} = settings || {};
    let polygons = this.data.polygons;

    if (!includeEmpty) {
      polygons = polygons.filter(polygon => polygon.points.length > 0);
    }

    if (includeLabeled !== undefined) {
      polygons = polygons.filter(polygon => {
        const hasLabel = polygon.label && polygon.label.trim() !== '';

        return includeLabeled ? hasLabel : !hasLabel;
      });
    }

    return polygons;
  }

  addPolygon(polygon: Partial<PolyLabelProps>): void {
    this.data.polygons.push({
      id: genId('POLY'),
      points: [],
      ...polygon,
    });
  }

  removePolygon(polygonId: PolyLabelProps['id']): boolean {
    let missionAccomplished = false;

    if (this.result) {
      const initialLength = this.data.polygons.length;
      this.data.polygons = this.data.polygons.filter(polygon => polygon.id !== polygonId);

      missionAccomplished = this.data.polygons.length < initialLength;
    }

    return missionAccomplished;
  }

  updatePolygonLabel(
    polygonId: PolyLabelProps['id'],
    label: PolyLabelProps['label']
  ): boolean {
    let missionAccomplished = false;

    if (this.data.polygons) {
      const polygons = this.data.polygons;
      const polygonIndex = polygons.findIndex(poly => poly.id === polygonId);

      if (polygonIndex !== -1) {
        polygons[polygonIndex].label = label;
        missionAccomplished = true;
      }
    }

    return missionAccomplished;
  }

  getResults(): SegmentationResult {
    return this.result;
  }

  getPolygonIndexForPoint(point: Point): number {
    let index = -1;

    if (this.result) {
      index = this.data.polygons.findIndex(polygon =>
        this.isPointInPolygon(point, polygon.id)
      );
    }

    return index;
  }

  editPolygonPoint(
    polygonId: PolyLabelProps['id'],
    oldPoint: Point,
    newPoint: Point
  ): boolean {
    let missionAccomplished = false;

    if (this.result) {
      const polygon = this.data.polygons.find(poly => poly.id === polygonId);

      if (polygon) {
        const pointIndex = polygon.points.findIndex(
          point => point.x === oldPoint.x && point.y === oldPoint.y
        );

        if (pointIndex !== -1) {
          polygon.points[pointIndex] = newPoint;
          missionAccomplished = true;
        }
      }
    }

    return missionAccomplished;
  }

  clearPolygons() {
    this.data.polygons = [];
  }

  private isPointInPolygon(point: Point, polygonId: PolyLabelProps['id']): boolean {
    let inside = false;
    const polygonPoints = this.data.polygons.find(poly => poly.id === polygonId)?.points;

    if (polygonPoints) {
      inside = isPointInPolygonUtil(point, polygonPoints);
    }

    return inside;
  }

  toString(): string {
    return `Segmentation for image ${this.imageId}: 
      Positive points: ${this.data.positivePoints.length},
      Negative points: ${this.data.negativePoints.length},
      Bounding boxes: ${this.data.boundingBoxes.length},
      Polygons: ${this.result ? this.data.polygons.length : 0}`;
  }

  toJSON(): any {
    return {
      imageId: this.imageId,
      data: {
        polygons: this.data.polygons
          .filter(polygon => polygon.points.length)
          .map(polygon => ({
            ...polygon,
            points: polygon.points.map(point => ({x: point.x, y: point.y})),
          })),
        boundingBoxes: this.data.boundingBoxes.map(box => ({...box})),
        positivePoints: this.data.positivePoints.map(point => ({x: point.x, y: point.y})),
        negativePoints: this.data.negativePoints.map(point => ({x: point.x, y: point.y})),
      },
    };
  }
}
